import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function About() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormState('success');
      return;
    }

    setFormState('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      formData.delete('phone_alt');

      const response = await fetch('https://readdy.ai/api/form/d9srci14ne17ei0sdmlg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试。');
        }
        setFormState('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试。');
      setFormState('error');
    }
  };

  return (
    <>
      <SchemaOrg type="AboutPage" data={{
        name: '关于人人美剧',
        description: '人人美剧是您追剧的最佳平台，提供海量最新美剧、经典美剧免费在线观看。',
        url: 'https://www.renrenmeiju.com/about',
        isPartOf: { '@type': 'WebSite', name: '人人美剧', url: 'https://www.renrenmeiju.com/' },
      }} />

      <Navbar />

      <main className="pt-16">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            <section className="mb-14">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-4">关于人人美剧</h1>
              <p className="text-foreground-600 leading-relaxed mb-4">
                人人美剧创立于2018年，是中国领先的美剧在线观看平台。我们致力于为中文观众提供最优质、最全面的美剧观看体验。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                从最初的几个人发展到如今数百万用户的规模，人人美剧始终坚持"让追剧成为一种享受"的初心。我们与全球多家内容提供商合作，确保第一时间为用户带来最新、最热的美剧资源。
              </p>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-950 mb-6">我们的优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { icon: 'ri-play-circle-line', title: '海量资源', desc: '超过数千部美剧资源，涵盖剧情、悬疑、科幻、喜剧、动作、恐怖、动画等全类型，满足不同观众的追剧需求。' },
                  { icon: 'ri-hd-line', title: '高清画质', desc: '支持720P/1080P高清播放，自适应码率技术确保流畅观看体验，即使在移动网络下也能畅享清晰视界。' },
                  { icon: 'ri-translate', title: '双语字幕', desc: '专业字幕团队精心翻译，提供中英文字幕自由切换。英文字幕助您学习英语，中文字幕让您轻松理解剧情。' },
                  { icon: 'ri-timer-line', title: '极速更新', desc: '热门美剧美国播出后24小时内更新中文字幕版本，让您第一时间同步追剧，不再担心被剧透。' },
                ].map((item) => (
                  <div key={item.title} className="p-5 bg-background-100 rounded-lg">
                    <span className="w-10 h-10 flex items-center justify-center bg-accent-100 text-accent-600 rounded-lg mb-3">
                      <i className={`${item.icon} text-lg`}></i>
                    </span>
                    <h3 className="font-heading text-sm text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-950 mb-6">用户口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: '小明同学', rating: 5, comment: '追了3年了，更新速度真的快！每次美剧刚播完第二天就能看到中文字幕版本，太给力了。画质也很清晰，手机上看一点都不卡。' },
                  { name: '追剧达人Lucy', rating: 5, comment: '资源真的很全！很多小众美剧在其他平台找不到，这里都有。中英双语字幕对学英语帮助很大，推荐给所有美剧爱好者。' },
                  { name: '电影爱好者阿杰', rating: 5, comment: '从大学就开始用人人美剧了，界面简洁好用，分类也很清晰。客服响应也很快，之前反馈的问题当天就解决了。' },
                ].map((review) => (
                  <div key={review.name} className="p-5 bg-background-100 rounded-lg">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <span key={i} className="w-4 h-4 flex items-center justify-center text-primary-500">
                          <i className="ri-star-fill text-sm"></i>
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{review.comment}&rdquo;</p>
                    <p className="text-xs text-foreground-500 font-medium">{review.name}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-950 mb-6">发展历程</h2>
              <div className="space-y-0">
                {[
                  { year: '2018', title: '平台创立', desc: '人人美剧正式上线，最初仅提供少量热门美剧资源，凭借优质的字幕和流畅的播放体验吸引首批用户。' },
                  { year: '2019', title: '快速发展', desc: '美剧资源扩展至千部级别，覆盖全类型。用户突破100万，成为中文美剧领域的重要平台。' },
                  { year: '2020', title: '技术升级', desc: '全面升级播放技术，支持自适应码率和高清画质。推出移动端优化版本，随时随地追剧。' },
                  { year: '2021', title: '内容扩展', desc: '引入更多独家资源和经典美剧。社区功能上线，用户可以分享追剧心得和观影评价。' },
                  { year: '2022', title: '品牌升级', desc: '品牌全面升级，用户突破500万。优化推荐算法，为用户提供更精准的个性化美剧推荐。' },
                  { year: '2023-至今', title: '持续创新', desc: '持续优化用户体验，丰富内容库。致力于成为中文用户最信赖的美剧在线观看平台。' },
                ].map((milestone, idx) => (
                  <div key={idx} className="flex gap-4 pb-6 relative">
                    {idx < 5 && (
                      <div className="absolute left-[15px] top-10 bottom-0 w-0.5 bg-background-200"></div>
                    )}
                    <div className="w-8 h-8 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0 text-xs font-bold z-10">
                      {idx + 1}
                    </div>
                    <div className="pt-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full whitespace-nowrap">{milestone.year}</span>
                        <h3 className="font-heading text-sm text-foreground-950">{milestone.title}</h3>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed">{milestone.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="font-heading text-xl text-foreground-950 mb-6">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    如果您有任何问题、建议或合作意向，请随时通过以下方式联系我们。我们的团队会在24小时内回复您的消息。
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm font-medium text-foreground-950">contact@renrenmeiju.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg flex-shrink-0">
                        <i className="ri-wechat-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">微信公众号</p>
                        <p className="text-sm font-medium text-foreground-950">人人美剧</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-lg flex-shrink-0">
                        <i className="ri-twitter-x-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">微博</p>
                        <p className="text-sm font-medium text-foreground-950">@人人美剧官方</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="font-heading text-base text-foreground-950 mb-4">发送消息</h3>
                  <form onSubmit={handleSubmit} data-readdy-form="">
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          type="text"
                          id="about-name"
                          name="name"
                          required
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          type="email"
                          id="about-email"
                          name="email"
                          required
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                        <input
                          type="text"
                          id="about-subject"
                          name="subject"
                          required
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="请输入消息主题"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">消息内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-md text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                          placeholder="请输入您的消息内容（最多500字）"
                        />
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        className="honeypot-field"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />

                      <button
                        type="submit"
                        disabled={formState === 'submitting'}
                        className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-background-50 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {formState === 'submitting' ? '发送中...' : '发送消息'}
                      </button>
                    </div>

                    {formState === 'success' && (
                      <div className="mt-4 p-3 bg-accent-100 text-accent-700 rounded-md text-sm">
                        <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-checkbox-circle-line"></i>
                        </span>
                        消息已发送成功！我们会尽快回复您。
                      </div>
                    )}
                    {formState === 'error' && formError && (
                      <div className="mt-4 p-3 bg-primary-100 text-primary-700 rounded-md text-sm">
                        <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-error-warning-line"></i>
                        </span>
                        {formError}
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}